import React, { createContext, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { flushSync } from "react-dom";
import { createTheme, ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { ptBR } from "@material-ui/core/locale";
import { CssBaseline } from "@material-ui/core";

const ThemeContext = createContext();

const CONSIG_GREEN = "#0D9488";
const CONSIG_GREEN_LIGHT = "#14b8a6";
const CONSIG_GREEN_DARK = "#0f766e";

const buildTheme = (darkMode) =>
  createTheme({

    

    
    typography: {
      fontFamily: "'Plus Jakarta Sans', 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      fontWeightLight: 400,
      fontWeightRegular: 500,
      fontWeightMedium: 600,
      fontWeightBold: 700,
      h1: { fontWeight: 700, letterSpacing: "-0.02em" },
      h2: { fontWeight: 700, letterSpacing: "-0.02em" },
      h3: { fontWeight: 700, letterSpacing: "-0.015em" },
      h4: { fontWeight: 700, letterSpacing: "-0.015em" },
      h5: { fontWeight: 700, letterSpacing: "-0.01em" },
      h6: { fontWeight: 700, letterSpacing: "-0.01em" },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      body1: { fontWeight: 500 },
      body2: { fontWeight: 500 },
      button: { fontWeight: 600, textTransform: "none" },
      caption: { fontWeight: 500 },
      overline: { fontWeight: 600, letterSpacing: "0.06em" },
    },
    scrollbarStyles: {
      "&::-webkit-scrollbar": {
        width: "9px",
        height: "9px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: darkMode ? "rgba(45,212,191,0.35)" : "rgba(13,148,136,0.45)",
        borderRadius: "8px",
        border: "2px solid transparent",
        backgroundClip: "content-box",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
      },
    },
    palette: {
      type: darkMode ? "dark" : "light",
      primary: {
        main: CONSIG_GREEN,
        light: CONSIG_GREEN_LIGHT,
        dark: CONSIG_GREEN_DARK,
        contrastText: "#ffffff",
      },
      secondary: {
        main: CONSIG_GREEN_LIGHT,
        contrastText: "#ffffff",
      },
      // Claro corporativo; escuro liso (sem tema espacial)
      background: darkMode
        ? { default: "#0b1312", paper: "#121c1b" }
        : { default: "#f4f7f7", paper: "#ffffff" },
      text: darkMode
        ? { primary: "#e2e8f0", secondary: "#94a3b8" }
        : { primary: "#0f2a28", secondary: "#5a706d" },
      ...(darkMode && { divider: "rgba(148,163,184,0.14)" }),
    },
    shape: {
      borderRadius: 8,
    },
    overrides: {
      
      ...(darkMode && {
        MuiTypography: {
          colorPrimary: {
            color: "#ffffff",
          },
        },
      }),
      MuiCssBaseline: {
        "@global": {

          
          "html, body, #root": {
            fontFamily:
              "'Plus Jakarta Sans', 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
            fontWeight: 500,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          "input, textarea, select, button, table, th, td, label": {
            fontFamily: "inherit",
          },
          "h1, h2, h3, h4, h5, h6, th, strong, b": {
            fontWeight: 700,
          },
          // barra de rolagem verde e visível nos dois modos
          "*::-webkit-scrollbar": { width: "9px", height: "9px" },
          "*::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: darkMode
              ? "rgba(45,212,191,0.35)"
              : "rgba(13,148,136,0.45)",
            borderRadius: "8px",
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
          "*::-webkit-scrollbar-thumb:hover": {
            backgroundColor: darkMode
              ? "rgba(45,212,191,0.6)"
              : "rgba(13,148,136,0.75)",
          },
          
          "input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active": {
            WebkitBoxShadow: `0 0 0 1000px ${darkMode ? "#121c1b" : "#ffffff"} inset`,
            WebkitTextFillColor: darkMode ? "#e2e8f0" : "#0f1a19",
            caretColor: darkMode ? "#e2e8f0" : "#0f1a19",
            transition: "background-color 9999s ease-in-out 0s",
          },
        },
      },
      // ── Visual novo do sistema inteiro (só aparência) ──
      MuiButton: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 10,
          transition: "background-color .2s ease, box-shadow .2s ease, transform .15s ease",
          "&:active": { transform: "scale(0.98)" },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
          boxShadow: "0 4px 12px rgba(20,184,166,0.25)",
          "&:hover": {
            background: "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)",
            boxShadow: "0 6px 16px rgba(20,184,166,0.35)",
          },
          "&.Mui-disabled": {
            background: darkMode ? "rgba(255,255,255,0.08)" : "#e4e7ec",
            boxShadow: "none",
          },
        },
        outlinedPrimary: {
          borderColor: "rgba(13,148,136,0.5)",
          "&:hover": { backgroundColor: "rgba(13,148,136,0.08)" },
        },
      },
      MuiIconButton: {
        root: {
          transition: "background-color .2s ease, color .2s ease, transform .15s ease",
          "&:active": { transform: "scale(0.94)" },
        },
      },
      MuiPaper: {
        rounded: { borderRadius: 12 },
        elevation1: {
          boxShadow: darkMode
            ? "0 1px 3px rgba(0,0,0,0.45)"
            : "0 1px 3px rgba(10,38,36,0.08), 0 1px 2px rgba(10,38,36,0.05)",
        },
        outlined: {
          border: darkMode ? "1px solid rgba(148,163,184,0.14)" : "1px solid #dcebe9",
        },
      },
      MuiDialog: {
        paper: {
          borderRadius: 18,
          ...(darkMode && {
            backgroundColor: "#121c1b",
            border: "1px solid rgba(148,163,184,0.14)",
          }),
        },
      },
      MuiDialogTitle: {
        root: {
          borderBottom: darkMode ? "1px solid rgba(148,163,184,0.14)" : "1px solid #ecf4f3",
          "& h2": { fontWeight: 700 },
        },
      },
      MuiDialogActions: {
        root: {
          padding: "12px 20px",
          borderTop: darkMode ? "1px solid rgba(148,163,184,0.14)" : "1px solid #ecf4f3",
        },
      },
      MuiMenu: {
        paper: {
          borderRadius: 12,
          border: darkMode ? "1px solid rgba(148,163,184,0.14)" : "1px solid #dcebe9",
          boxShadow: darkMode
            ? "0 12px 32px rgba(0,0,0,0.5)"
            : "0 12px 32px rgba(10,38,36,0.12)",
        },
      },
      MuiMenuItem: {
        root: {
          borderRadius: 8,
          margin: "1px 6px",
          fontSize: "0.9rem",
          "&:hover": { backgroundColor: "rgba(13,148,136,0.08)" },
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: "rgba(13,148,136,0.14)",
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: 10,
          transition: "box-shadow .2s ease",
          "&:hover $notchedOutline": {
            borderColor: "rgba(13,148,136,0.6)",
          },
          "&.Mui-focused": {
            boxShadow: "0 0 0 3px rgba(13,148,136,0.14)",
          },
        },
        notchedOutline: {
          borderColor: darkMode ? "rgba(255,255,255,0.12)" : "#dfe7e2",
        },
      },
      MuiTooltip: {
        tooltip: {
          backgroundColor: darkMode ? "#1e293b" : "#0f2a1c",
          border: "1px solid rgba(45,212,191,0.25)",
          fontSize: "0.75rem",
          fontWeight: 500,
          borderRadius: 8,
          padding: "6px 10px",
        },
        arrow: { color: darkMode ? "#1e293b" : "#0f2a1c" },
      },
      MuiChip: {
        root: { fontWeight: 600, borderRadius: 8 },
      },
      MuiSwitch: {
        switchBase: {
          "&$checked": { color: "#14b8a6" },
          "&$checked + $track": { backgroundColor: "#14b8a6", opacity: 0.5 },
        },
      },
      MuiTabs: {
        indicator: { borderRadius: 3, height: 3 },
      },
      MuiTab: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          "&.Mui-selected": { color: "#0D9488" },
        },
      },
      MuiTableRow: {
        root: {
          transition: "background-color .15s ease",
          "&$hover:hover": {
            backgroundColor: darkMode ? "rgba(148,163,184,0.06)" : "#f4faf6",
          },
        },
      },
      MuiTableHead: {
        root: {
          backgroundColor: darkMode ? "#0e1716" : "#f4f8f5",
        },
      },
      MuiTableCell: {
        head: {
          fontWeight: 700,
          color: darkMode ? "#6ee7a0" : "#0D9488",
          fontSize: "0.72rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          borderBottom: darkMode ? "1px solid #1e293b" : "1px solid #dcebe1",
        },
        body: {
          color: darkMode ? "#cbd5e1" : "#1f3a2b",
          borderBottom: darkMode ? "1px solid #182625" : "1px solid #edf4ef",
        },
      },
    },
  }, ptBR);

export const ThemeProvider = ({ children }) => {
  // O tema espacial (escuro) virou o padrão. A chave é nova de propósito:
  // quem tinha "claro" salvo na chave antiga também passa a ver o tema novo,
  // e continua podendo voltar para o claro pelo menu do usuário.
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("temaEspacial") === "true" // padrao: claro
  );

  const toggleTheme = () => {
    const trocar = () => {
      // flushSync: a tela nova precisa estar pronta dentro do
      // startViewTransition para o navegador fazer o fade entre as duas
      flushSync(() => {
        setDarkMode((prevMode) => {
          const next = !prevMode;
          localStorage.setItem("temaEspacial", String(next));
          return next;
        });
      });
    };

    // Sem transições CSS durante a troca: antes cada elemento mudava de cor
    // num ritmo diferente e a tela ficava "quebrada" por um instante.
    const html = document.documentElement;
    html.classList.add("trocando-tema");
    const liberar = () => setTimeout(() => html.classList.remove("trocando-tema"), 50);

    if (document.startViewTransition) {
      const vt = document.startViewTransition(trocar);
      vt.finished.finally(liberar);
    } else {
      trocar();
      liberar();
    }
  };

  const theme = useMemo(() => buildTheme(darkMode), [darkMode]);

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
